/**
 * Booking Holds Service
 * API client for booking holds management
 * Sessions are managed server-side via httpOnly cookies
 */

import { apiRequest } from './api';

/**
 * Create a booking hold for a specific date
 * @param {Object} holdData - Hold data
 * @param {string} holdData.guide_id - Guide ID
 * @param {string} holdData.guide_service_id - Guide service ID
 * @param {string} holdData.date - Date in YYYY-MM-DD format
 * @returns {Promise<Object>} Hold creation result
 */
export async function createHold(holdData) {
  return apiRequest('/api/v1/booking-holds', {
    method: 'POST',
    body: JSON.stringify(holdData),
  });
}

/**
 * Extend an existing booking hold
 * @param {string} holdId - Hold ID
 * @param {number} checkoutStep - Current checkout step
 * @param {string} actionType - Action type (e.g., 'step_advanced')
 * @returns {Promise<Object>} Extended hold
 */
export async function extendHold(holdId, checkoutStep, actionType) {
  return apiRequest(`/api/v1/booking-holds/${holdId}/extend`, {
    method: 'PUT',
    body: JSON.stringify({ checkout_step: checkoutStep, action_type: actionType }),
  });
}

/**
 * Release a booking hold
 * @param {string} holdId - Hold ID
 * @returns {Promise<Object>} Release result
 */
export async function releaseHold(holdId) {
  return apiRequest(`/api/v1/booking-holds/${holdId}`, {
    method: 'DELETE',
  });
}

/**
 * Validate a booking hold
 * @param {string} holdId - Hold ID
 * @returns {Promise<Object>} Validation result
 */
export async function validateHold(holdId) {
  return apiRequest(`/api/v1/booking-holds/${holdId}/validate`, {
    method: 'POST',
  });
}

/**
 * Get all holds for current user/session
 * @returns {Promise<Array>} User holds
 */
export async function getUserHolds() {
  return apiRequest('/api/v1/booking-holds/user', {
    method: 'GET',
  });
}

/**
 * Get date availability status for a guide service
 * @param {string} guideId - Guide ID
 * @param {string} guideServiceId - Guide service ID
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @returns {Promise<Object>} Date availability map
 */
export async function getDateAvailability(guideId, guideServiceId, startDate, endDate) {
  const params = new URLSearchParams({
    startDate,
    endDate,
    experienceId: guideServiceId,
  });

  return apiRequest(`/api/v1/availability/${guideId}/calendar?${params}`, {
    method: 'GET',
  });
}
